import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useToastStore } from '@rideiq/shared/stores/toastStore';

import {
  addRider,
  updatePartialRider,
  updatePartialRiderWithPhoto,
  validateRiderDetails,
} from '../../../api/services/Riders';

import { didAgeIntoBoosterRange } from './helpers/didAgeIntoBoosterRange';
import { dispatchLocationDrafts } from './utils/dispatchLocationDrafts';
import { mapApiErrorsToForm } from './utils/mapApiErrorsToForm';
import { findFirstErrorSection } from './utils/sectionFields';
import { toCreateRiderPayload, toPatchRiderPayload } from './utils/toRiderPayload';
import type { RiderFormValues } from './types';
import { SectionKey } from './types';

type SubmitMode = 'create' | 'edit';

type UseRiderFormSubmitArgs = {
  mode: SubmitMode;
  slug?: string;
  reportSave: (structuredFieldsSet: string[], hasMustBeMet: boolean, sisImportUsed: boolean) => void;
  onCreateConfirmation?: (slug: string) => void;
  originalIsSharedBillingRider?: boolean;
};

export type SubmitOutcome =
  | { kind: 'ok'; slug: string }
  | { kind: 'partial-failure'; slug: string }
  | { kind: 'rider-error' };

type DirtyFields = Partial<Record<keyof RiderFormValues, unknown>>;

type UseRiderFormSubmitReturn = {
  onSubmit: (
    values: RiderFormValues,
    dirtyFields?: DirtyFields,
    confirmedSharedBilling?: boolean
  ) => Promise<SubmitOutcome>;
  scrollToFirstError: (
    expandSection: (key: SectionKey) => void,
    errors: Parameters<typeof findFirstErrorSection>[0]
  ) => void;
};

const CREATE_SUCCESS_TOAST = 'Rider created.';
const EDIT_SUCCESS_TOAST = 'Rider updated.';
const BOOSTER_AGE_TOAST =
  'Rider updated. Their booster or car seat setting was adjusted automatically based on the new date of birth, please review.';
const PARTIAL_TOAST = 'Rider saved, but some locations failed to update. Try again from the View Rider page.';

const getEditSuccessToastMessage = (mode: SubmitMode, agedIntoBoosterRange: boolean): string => {
  if (mode === 'create') {
    return CREATE_SUCCESS_TOAST;
  }

  return agedIntoBoosterRange ? BOOSTER_AGE_TOAST : EDIT_SUCCESS_TOAST;
};

export const useRiderFormSubmit = ({
  mode,
  slug,
  reportSave,
  onCreateConfirmation,
  originalIsSharedBillingRider,
}: UseRiderFormSubmitArgs): UseRiderFormSubmitReturn => {
  const navigate = useNavigate();
  const { addToast } = useToastStore();
  const methods = useFormContext<RiderFormValues>();

  const setServiceConflictMessages = useCallback(
    (messages: string[]) => methods.setValue('serviceConflictMessages', messages),
    [methods]
  );

  const onSubmit = useCallback(
    async (
      values: RiderFormValues,
      dirtyFields?: DirtyFields,
      confirmedSharedBilling = false
    ): Promise<SubmitOutcome> => {
      let resolvedSlug: string;

      if (mode === 'create') {
        const payload = toCreateRiderPayload(values);

        // Validate against the backend before creating — the API applies stricter checks
        // (e.g. email format/domain) than the client-side Yup schema, and this endpoint
        // returns structured, per-field errors that map cleanly onto form fields, unlike
        // the create endpoint's flat, unstructured validation-failure messages.
        //
        // `services` is omitted: Riders::Validator forwards it straight into
        // Passenger#assign_attributes, and `services` is a has_many-through association —
        // assigning our string codes crashes with ActiveRecord::RecordNotFound (a 500).
        const { services: _validationServices, ...validationPayload } = payload;
        const validation = await validateRiderDetails(validationPayload as never);

        if (validation.type === 'error') {
          mapApiErrorsToForm(
            validation.data as Record<string, unknown>,
            methods.setError,
            addToast,
            setServiceConflictMessages
          );

          return { kind: 'rider-error' };
        }

        const writeResponse = await addRider(payload as never);

        if (writeResponse.type === 'error') {
          mapApiErrorsToForm(
            writeResponse.data as Record<string, unknown>,
            methods.setError,
            addToast,
            setServiceConflictMessages
          );

          return { kind: 'rider-error' };
        }

        resolvedSlug = (writeResponse.data as { slug?: string })?.slug ?? '';
      } else {
        const effectiveDirtyFields =
          dirtyFields ?? (methods.formState.dirtyFields as Partial<Record<keyof RiderFormValues, unknown>>);
        const originalContactIds = methods.getValues('originalContactIds');
        const payload = toPatchRiderPayload(
          values,
          effectiveDirtyFields,
          confirmedSharedBilling,
          originalContactIds,
          originalIsSharedBillingRider
        );

        if (Object.keys(payload).length > 0) {
          const hasPhotoUpload =
            (payload.photosAttributes as Array<{ attachment?: unknown }>)?.[0]?.attachment instanceof File;
          const writeResponse = hasPhotoUpload
            ? await updatePartialRiderWithPhoto(slug as string, payload as never)
            : await updatePartialRider(slug as string, payload as never);

          if (writeResponse.type === 'error') {
            mapApiErrorsToForm(
              writeResponse.data as Record<string, unknown>,
              methods.setError,
              addToast,
              setServiceConflictMessages
            );

            return { kind: 'rider-error' };
          }
        }

        resolvedSlug = slug as string;
      }

      // originalLocationIds is not watched, so read directly from the form store —
      // handleSubmit's values snapshot may omit unregistered fields.
      const originalLocationIds = methods.getValues('originalLocationIds');

      const locationOutcome = await dispatchLocationDrafts({
        slug: resolvedSlug,
        drafts: values.locationDrafts,
        originalLocationIds,
      });

      const structuredFieldsSet = values.supportSettings.filter((s) => s.selected).map((s) => s.supportType);
      const hasMustBeMet = values.locationDrafts.some((draft) => draft.formData.mustBeMet);

      reportSave(structuredFieldsSet, hasMustBeMet, methods.getValues('sisImportUsed'));

      if (locationOutcome.kind === 'partial-failure') {
        addToast({ severity: 'warning', message: PARTIAL_TOAST });
        navigate(`/riders/${resolvedSlug}`);

        return { kind: 'partial-failure', slug: resolvedSlug };
      }

      if (onCreateConfirmation) {
        onCreateConfirmation(resolvedSlug);
      } else {
        const agedIntoBoosterRange =
          mode === 'edit' && didAgeIntoBoosterRange(methods.formState.defaultValues?.dateOfBirth, values.dateOfBirth);

        const successMessage = getEditSuccessToastMessage(mode, agedIntoBoosterRange);

        addToast({
          severity: 'success',
          message: successMessage,
          sticky: true,
        });
        navigate(`/riders/${resolvedSlug}`);
      }

      return { kind: 'ok', slug: resolvedSlug };
    },
    [
      mode,
      slug,
      methods,
      addToast,
      navigate,
      reportSave,
      onCreateConfirmation,
      setServiceConflictMessages,
      originalIsSharedBillingRider,
    ]
  );

  const scrollToFirstError = useCallback(
    (expandSection: (key: SectionKey) => void, errors: Parameters<typeof findFirstErrorSection>[0]) => {
      const sectionKey = findFirstErrorSection(errors);

      if (!sectionKey) {
        return;
      }

      expandSection(sectionKey);

      requestAnimationFrame(() => {
        document.getElementById(`section-${sectionKey}`)?.scrollIntoView({
          behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center',
        });
      });
    },
    []
  );

  return { onSubmit, scrollToFirstError };
};
